import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useApiCall } from '../hooks/useApiCall';
import { 
  RiArrowLeftLine,
  RiSecurePaymentLine,
  RiTruckLine,
  RiMapPinLine,
  RiPhoneLine,
  RiUserLine,
  RiCashLine,
  RiCheckLine,
  RiLoader4Line,
  RiShoppingBagLine
} from 'react-icons/ri';
import '../styles/Checkout.css';

export default function Checkout() {
  const navigate = useNavigate();
  const { account } = useAccount();
  const { refreshCounts } = useCart();
  const { showSuccess, showError } = useToast();
  const apiCall = useApiCall();

  // State for form fields
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('delivery');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [loadingCart, setLoadingCart] = useState(true);
  const [comments, setComments] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [contactInfo, setContactInfo] = useState({
    name: '',
    phone: ''
  });
  
  const [deliveryAddress, setDeliveryAddress] = useState({
    address: '',
    address_num: ''
  });

  // Fetch cart on mount and auto-fill contact info
  useEffect(() => {
    if (account?.token) {
      fetchCart();
      // Auto-fill contact information from account
      if (account.name) {
        setContactInfo(prev => ({ ...prev, name: account.name }));
      }

      console.log(account)
      
      if (account.phone) {
        setContactInfo(prev => ({ ...prev, phone: account.phone }));
      }
    }
    // eslint-disable-next-line
  }, [account]);

  // Redirect if cart is empty after loading
  useEffect(() => {
    if (!loadingCart && (!cart || cart.length === 0)) {
      showError('Your cart is empty');
      navigate('/cart');
    }
    // eslint-disable-next-line
  }, [loadingCart, cart]);

  const fetchCart = async () => {
    if (!account?.token) return;
    
    setLoadingCart(true);
    try {
      const response = await apiCall('account/cart/get', 'POST', {}, account, null);
      
      if (response.success) {
        setCart(response.data.cart || []);
      } else {
        showError('Failed to load cart');
        console.error('Cart fetch failed:', response.message);
      }
    } catch (error) {
      showError('Failed to load cart');
      console.error('Error fetching cart:', error);
    } finally {
      setLoadingCart(false);
    }
  };

  const handleContactChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setDeliveryAddress(prev => ({ ...prev, [field]: value }));
  };

  const getCurrency = () => {
    // Get currency from first cart item, default to USD if not available
    if (cart && cart.length > 0 && cart[0].price_curr) {
      return cart[0].price_curr;
    }
    return 'USD';
  };

  const getCurrencySymbol = () => {
    const currency = getCurrency();
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'SRD': '$'
    };
    return symbols[currency] || '$';
  };

  const calculateSubtotal = () => {
    if (!cart || cart.length === 0) return 0;
    return cart.reduce((total, item) => {
      const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
      const basePrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
      const quantity = parseInt(item.qty) || 0;
      
      // Modifier amount replaces base price (versions/variants)
      let unitPrice = basePrice;
      if (item.options) {
        try {
          const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
          if (options && options.modifiers && Array.isArray(options.modifiers)) {
            const priceModifier = options.modifiers.find(m => m.option && parseFloat(m.option.amount) > 0);
            if (priceModifier) unitPrice = parseFloat(priceModifier.option.amount);
          }
        } catch (e) {
          console.error('Error parsing options for price calculation:', e);
        }
      }
      
      return total + unitPrice * quantity;
    }, 0);
  };

  const calculateDeliveryFee = () => {
    return orderType === 'delivery' ? 0 : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateDeliveryFee();
  };

  const validateForm = () => {
    if (!contactInfo.name.trim() || !contactInfo.phone.trim()) return false;
    if (orderType === 'delivery' && (!deliveryAddress.address.trim() || !deliveryAddress.address_num.trim())) return false;
    return true;
  };

  const handlePlaceOrderClick = () => {
    if (!validateForm()) {
      showError('Please fill in all required fields');
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmOrder = async () => {
    setShowConfirmModal(false);
    setLoading(true);
    
    try {
      const contact = {
        contact: contactInfo.phone,
        name: contactInfo.name
      };

      const orderData = {
        type: orderType,
        contact: JSON.stringify(contact),
        address: orderType === 'delivery' ? deliveryAddress.address : null,
        address_num: orderType === 'delivery' ? deliveryAddress.address_num : null,
        payment_method: paymentMethod,
        comments: comments || null
      };

      const response = await apiCall('account/order/place', 'POST', orderData, account, null);
      
      if (response.success) {
        showSuccess('Order placed successfully!');
        refreshCounts(); // Refresh cart count badge
        navigate(`/account/order/${response.data.order_id}`);
      } else {
        showError(response.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      showError('An error occurred while placing your order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = () => {
    setShowConfirmModal(false);
  };

  if (loadingCart) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="loading-state">
            <RiLoader4Line className="loading-icon" />
            <p>Loading checkout...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!cart || cart.length === 0) {
    return null;
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-header">
          <button 
            className="btn-checkout-back"
            onClick={() => navigate('/cart')}
          >
            <RiArrowLeftLine />
            Back to cart
          </button>
          <h1 className="checkout-title">Checkout</h1>
        </div>

        <div className="checkout-content">
          <div className="checkout-form">
            <div className="form-section">
              <h3 className="checkout-section-title">Order type</h3>
              <div className="order-type-options">
                <label className={`order-type-option ${orderType === 'delivery' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="delivery"
                    checked={orderType === 'delivery'}
                    onChange={(e) => setOrderType(e.target.value)}
                  />
                  <div className="option-content">
                    <RiTruckLine className="option-icon" />
                    <div>
                      <div className="option-title">Delivery</div>
                      <div className="option-subtitle">Get your order delivered</div>
                    </div>
                  </div>
                </label>
                
                <label className={`order-type-option ${orderType === 'pickup' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="pickup"
                    checked={orderType === 'pickup'}
                    onChange={(e) => setOrderType(e.target.value)}
                  />
                  <div className="option-content">
                    <RiShoppingBagLine className="option-icon" />
                    <div>
                      <div className="option-title">Pickup</div>
                      <div className="option-subtitle">Collect from store</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3 className="checkout-section-title">Contact information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    <RiUserLine className="label-icon" />
                    Full name *
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your full name"
                    value={contactInfo.name}
                    onChange={(e) => handleContactChange('name', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <RiPhoneLine className="label-icon" />
                    Phone number *
                  </label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="Enter your phone number"
                    value={contactInfo.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {orderType === 'delivery' && (
              <div className="form-section">
                <h3 className="checkout-section-title">Delivery address</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">
                      <RiMapPinLine className="label-icon" />
                      Street address *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter street name"
                      value={deliveryAddress.address}
                      onChange={(e) => handleAddressChange('address', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">House/Building number *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter number"
                      value={deliveryAddress.address_num}
                      onChange={(e) => handleAddressChange('address_num', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-section">
              <h3 className="checkout-section-title">Payment method</h3>
              <div className="payment-options">
                <label className={`payment-option ${paymentMethod === 'cash' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    value="cash"
                    checked={paymentMethod === 'cash'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="payment-content">
                    <RiCashLine className="payment-icon" />
                    <div>
                      <div className="payment-title">Cash on {orderType === 'delivery' ? 'delivery' : 'pickup'}</div>
                      <div className="payment-subtitle">Pay when you receive your order</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3 className="checkout-section-title">Special instructions</h3>
              <div className="form-group">
                <label className="form-label">Comments (optional)</label>
                <textarea
                  className="form-textarea"
                  placeholder="Any special instructions for your order..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </div>

          <div className="checkout-summary">
            <div className="summary-section">
              <h3 className="summary-title">Your order</h3>
              <div className="order-items">
                {cart.map(item => {
                  const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
                  const price = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
                  const quantity = parseInt(item.qty) || 0;
                  
                  let options = null;
                  let unitPrice = price;
                  if (item.options) {
                    try {
                      options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                      // Modifier amount replaces base price (versions/variants)
                      if (options && options.modifiers && Array.isArray(options.modifiers)) {
                        const priceModifier = options.modifiers.find(m => m.option && parseFloat(m.option.amount) > 0);
                        if (priceModifier) unitPrice = parseFloat(priceModifier.option.amount);
                      }
                    } catch (e) {
                      console.error('Error parsing options:', e);
                    }
                  }
                  let itemTotal = unitPrice * quantity;
                  
                  // Create slug for product URL
                  const createSlug = (name) => {
                    return name
                      .toLowerCase()
                      .replace(/[^a-z0-9\s&]/g, '')
                      .replace(/\s*&\s*/g, '-and-')
                      .replace(/\s+/g, '-')
                      .replace(/-+/g, '-')
                      .replace(/^-|-$/g, '');
                  };
                  const productSlug = createSlug(item.name);
                  
                  return (
                    <div key={item.cart_id} className="order-item">
                      <div 
                        className="order-item-image"
                        onClick={() => navigate(`/products/product/${item.id}/${productSlug}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img src={item.primary_image} alt={item.name} />
                      </div>
                      <div className="order-item-details">
                        <div 
                          className="order-item-name"
                          onClick={() => navigate(`/products/product/${item.id}/${productSlug}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {item.name}
                        </div>
                        {options && options.modifiers && options.modifiers.length > 0 && (
                          <div className="order-item-options">
                            {options.modifiers.map((modifier, index) => (
                              <div key={index} className="modifier-item">
                                <strong>{modifier.name}:</strong> {modifier.option?.item || 'N/A'}
                                {modifier.option?.amount > 0 && <span className="modifier-price"> (+${parseFloat(modifier.option.amount).toFixed(2)})</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="order-item-qty">Qty: {quantity}</div>
                      </div>
                      <div className="order-item-price">{getCurrencySymbol()} {itemTotal.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="summary-section">
              <h3 className="summary-title">Order total</h3>
              <div className="summary-line">
                <span>Subtotal</span>
                <span>{getCurrencySymbol()} {calculateSubtotal().toFixed(2)}</span>
              </div>
              {orderType === 'delivery' && (
                <div className="summary-line">
                  <span>Delivery fee</span>
                  <span>{getCurrencySymbol()} {calculateDeliveryFee().toFixed(2)}</span>
                </div>
              )}
              <div className="summary-line total">
                <span>Total</span>
                <span>{getCurrencySymbol()} {calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            <div className="checkout-features">
              <div className="feature-item">
                <RiSecurePaymentLine className="feature-icon" />
                <span>Secure payment</span>
              </div>
              <div className="feature-item">
                <RiCheckLine className="feature-icon" />
                <span>Order protection</span>
              </div>
            </div>

            <button 
              className="btn-place-order"
              onClick={handlePlaceOrderClick}
              disabled={loading || !validateForm()}
            >
              {loading ? (
                <>
                  <RiLoader4Line className="loading-icon" />
                  Processing...
                </>
              ) : (
                <>
                  Place order - {getCurrencySymbol()}{calculateTotal().toFixed(2)}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={handleCancelOrder}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm your order</h2>
            </div>
            <div className="modal-body">
              <div className="confirm-section">
                <h4>Order type</h4>
                <p>{orderType === 'delivery' ? 'Delivery' : 'Pickup'}</p>
              </div>
              
              <div className="confirm-section">
                <h4>Contact</h4>
                <p>{contactInfo.name}</p>
                <p>{contactInfo.phone}</p>
              </div>
              
              {orderType === 'delivery' && (
                <div className="confirm-section">
                  <h4>Delivery address</h4>
                  <p>{deliveryAddress.address} {deliveryAddress.address_num}</p>
                </div>
              )}
              
              <div className="confirm-section">
                <h4>Payment method</h4>
                <p>Cash on {orderType === 'delivery' ? 'delivery' : 'pickup'}</p>
              </div>
              
              {comments && (
                <div className="confirm-section">
                  <h4>Special instructions</h4>
                  <p>{comments}</p>
                </div>
              )}
              
              <div className="confirm-section total-section">
                <h4>Order total</h4>
                <p className="total-amount">{getCurrencySymbol()} {calculateTotal().toFixed(2)}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCancelOrder}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={handleConfirmOrder}>
                Confirm order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
